import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Phone, CreditCard, Loader2, CheckCircle2, Clock, XCircle, Copy, Sparkles } from "lucide-react";

const OPERATOR_COLORS = {
  mtn: "#FFCC00",
  orange: "#FF6F00",
};

function buildUssdLink(template, amount) {
  const code = template.replace("{amount}", String(amount));
  // tel: URL: encode # as %23 (RFC compliant); keep * as-is (most dialers accept it).
  return `tel:${code.replace(/#/g, "%23")}`;
}

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}

const STATUS_META = {
  pending: { label: "En attente", icon: Clock, color: "#F59E0B", bg: "#FEF3C7" },
  approved: { label: "Approuvée", icon: CheckCircle2, color: "#10B981", bg: "#D1FAE5" },
  rejected: { label: "Rejetée", icon: XCircle, color: "#EF4444", bg: "#FEE2E2" },
};

export default function TFTRecharge({ onBalanceChange }) {
  const [options, setOptions] = useState(null);
  const [operator, setOperator] = useState("mtn");
  const [amount, setAmount] = useState(5000);
  const [phone, setPhone] = useState("");
  const [reference, setReference] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loadingReqs, setLoadingReqs] = useState(false);
  const [step, setStep] = useState(1); // 1 = choose, 2 = pay/ref
  const [copiedCode, setCopiedCode] = useState(false);

  const loadRequests = useCallback(async () => {
    setLoadingReqs(true);
    try {
      const { data } = await api.get("/driver/tft/payment-requests");
      setRequests(data || []);
    } catch { /* noop */ }
    finally { setLoadingReqs(false); }
  }, []);

  useEffect(() => {
    api.get("/tft/recharge-options").then(({ data }) => {
      setOptions(data);
      // If our default operator was disabled by admin, fall back to the first available one
      const keys = Object.keys(data?.operators || {});
      if (keys.length && !keys.includes(operator)) {
        setOperator(keys[0]);
      }
    }).catch(() => {});
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRequests]);

  const ussdCode = options?.operators?.[operator]?.ussd_template?.replace("{amount}", String(amount)) || "";
  const bonus = Math.floor(amount * (options?.bonus_rate || 0.34));
  const total = amount + bonus;

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(ussdCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 1800);
    } catch { /* noop */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reference || reference.trim().length < 4) {
      toast.error("Veuillez saisir la référence (4 caractères minimum)");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/driver/tft/payment-request", {
        operator,
        amount,
        reference: reference.trim(),
        phone_number: phone.trim(),
      });
      toast.success("Demande envoyée", { description: "Elle sera validée par l'administrateur sous peu." });
      setReference("");
      setStep(1);
      onBalanceChange?.();
      loadRequests();
    } catch (err) {
      toast.error("Échec de la demande", { description: err.response?.data?.detail || "Erreur serveur" });
    } finally {
      setSubmitting(false);
    }
  };

  if (!options) {
    return (
      <div className="bg-white rounded-2xl border border-[#CBD5E1] p-8 flex justify-center" data-testid="tft-recharge-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#0A2540]" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="tft-recharge-panel">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0A2540] to-[#1E3A5F] rounded-2xl p-5 text-white">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-[#FACC15]" />
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>Recharger mon compte TFT</h2>
        </div>
        <p className="text-sm text-white/80">Achetez du crédit TFT via MTN Mobile Money ou Orange Money. <span className="text-[#FACC15] font-semibold">Bonus +{Math.round((options.bonus_rate || 0.34) * 100)}% offert sur chaque recharge !</span></p>
      </div>

      {step === 1 && (
        <div className="bg-white rounded-2xl border border-[#CBD5E1] p-5 space-y-5">
          {Object.keys(options.operators).length === 0 ? (
            <div className="text-center py-8" data-testid="no-operators-msg">
              <p className="text-sm text-[#64748B]">Aucun opérateur de recharge n&apos;est actuellement disponible. Veuillez contacter l&apos;administration TrigoTour.</p>
            </div>
          ) : (
            <>
          <div>
            <p className="text-xs font-semibold text-[#0A2540] mb-2 uppercase tracking-wide">1. Choisissez votre opérateur</p>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(options.operators).map(([k, v]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setOperator(k)}
                  className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center justify-center gap-1.5 ${operator === k ? "border-[#0A2540] bg-[#0A2540]/5" : "border-[#CBD5E1] hover:border-[#0A2540]/50"}`}
                  data-testid={`tft-recharge-operator-${k}`}
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: OPERATOR_COLORS[k] || "#94A3B8" }}>
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-sm font-semibold text-[#0A2540]">{v.label}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-[#0A2540] mb-2 uppercase tracking-wide">2. Choisissez le montant</p>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {options.amounts.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAmount(a)}
                  className={`py-3 rounded-xl text-sm font-bold border-2 transition-all ${amount === a ? "border-[#FACC15] bg-[#FACC15] text-[#0A2540]" : "border-[#CBD5E1] bg-white text-[#0A2540] hover:border-[#FACC15]"}`}
                  data-testid={`tft-recharge-amount-${a}`}
                >
                  {a.toLocaleString()}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#FACC15]/10 to-[#0A2540]/5 border border-[#FACC15]/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[#64748B]">Achat</span>
              <span className="text-sm font-semibold text-[#0A2540]">{amount.toLocaleString()} XAF</span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-[#10B981] flex items-center gap-1"><Sparkles className="w-3 h-3" /> Bonus +{Math.round((options.bonus_rate || 0.34) * 100)}%</span>
              <span className="text-sm font-semibold text-[#10B981]">+{bonus.toLocaleString()} TFT</span>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-[#FACC15]/30">
              <span className="text-sm font-bold text-[#0A2540]">Total crédité</span>
              <span className="text-xl font-extrabold text-[#0A2540]" style={{ fontFamily: 'Outfit' }} data-testid="tft-recharge-total">{total.toLocaleString()} TFT</span>
            </div>
          </div>

          <Button onClick={() => setStep(2)} className="w-full bg-[#0A2540] text-white hover:bg-[#081b2e] h-11 font-semibold" data-testid="tft-recharge-continue">
            Continuer vers le paiement
          </Button>
            </>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="bg-white rounded-2xl border border-[#CBD5E1] p-5 space-y-4">
          <button onClick={() => setStep(1)} className="text-xs text-[#64748B] hover:text-[#0A2540] -ml-1" data-testid="tft-recharge-back">← Modifier la sélection</button>

          <div className="bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl p-4 space-y-2">
            <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wide">Récapitulatif</p>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#64748B]">Opérateur</span>
              <span className="font-semibold text-[#0A2540]">{options.operators[operator]?.label}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#64748B]">Montant</span>
              <span className="font-semibold text-[#0A2540]">{amount.toLocaleString()} XAF</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#10B981]">Vous recevrez</span>
              <span className="font-bold text-[#10B981]">{total.toLocaleString()} TFT (+{bonus} bonus)</span>
            </div>
          </div>

          <div className="bg-[#FFF8E1] border border-[#FACC15] rounded-xl p-4">
            <p className="text-xs font-bold text-[#0A2540] mb-2 uppercase tracking-wide">Étape 1: Composer le code USSD</p>
            <p className="text-xs text-[#64748B] mb-3">Sur votre téléphone {options.operators[operator]?.label}, composez:</p>
            <div className="bg-white border border-[#CBD5E1] rounded-lg p-3 flex items-center justify-between gap-2">
              <code className="text-sm font-mono text-[#0A2540] font-bold flex-1 break-all" data-testid="tft-recharge-ussd-code">{ussdCode}</code>
              <button onClick={handleCopyCode} className="shrink-0 p-1.5 rounded-md hover:bg-[#F8FAFC]" title="Copier le code" data-testid="tft-recharge-copy-code">
                {copiedCode ? <CheckCircle2 className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4 text-[#64748B]" />}
              </button>
            </div>
            <a
              href={buildUssdLink(options.operators[operator]?.ussd_template || "", amount)}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 h-11 rounded-lg bg-[#FACC15] text-[#0A2540] font-bold text-sm hover:bg-[#EAB308] transition-colors"
              data-testid="tft-recharge-dial-btn"
            >
              <Phone className="w-4 h-4" /> Composer maintenant
            </a>
            <p className="text-[10px] text-[#64748B] mt-2">Si le bouton n&apos;ouvre pas le composeur, copiez le code et composez-le manuellement. Confirmez avec votre code PIN Mobile Money.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-[#0A2540] uppercase tracking-wide block mb-1.5">Étape 2: Numéro de téléphone (optionnel)</label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Ex: +237 6XX XX XX XX" className="h-10 border-[#CBD5E1]" data-testid="tft-recharge-phone" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#0A2540] uppercase tracking-wide block mb-1.5">Étape 3: Référence de la transaction *</label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Ex: MP123456789" className="h-10 border-[#CBD5E1]" data-testid="tft-recharge-reference" required />
              <p className="text-[10px] text-[#64748B] mt-1">Vous recevez la référence par SMS après confirmation. Elle commence souvent par MP, OM ou un code numérique.</p>
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-[#0A2540] text-white hover:bg-[#081b2e] h-11 font-semibold" data-testid="tft-recharge-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <CreditCard className="w-4 h-4 mr-1.5" />}
              Envoyer la demande de validation
            </Button>
            <p className="text-[10px] text-center text-[#64748B]">Votre demande sera vérifiée par notre équipe et créditée généralement sous 1h.</p>
          </form>
        </div>
      )}

      {/* Requests history */}
      <div className="bg-white rounded-2xl border border-[#CBD5E1] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#CBD5E1] flex items-center justify-between">
          <p className="text-sm font-semibold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Mes demandes de recharge</p>
          <span className="text-xs text-[#64748B]">{requests.length} demande(s)</span>
        </div>
        {loadingReqs ? (
          <div className="py-8 flex justify-center text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin" /></div>
        ) : requests.length === 0 ? (
          <div className="py-10 text-center text-[#64748B]">
            <CreditCard className="w-7 h-7 mx-auto mb-1 opacity-40" />
            <p className="text-sm">Aucune demande de recharge pour le moment</p>
          </div>
        ) : (
          <ul className="divide-y divide-[#E2E8F0]">
            {requests.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.pending;
              const Icon = meta.icon;
              return (
                <li key={r.id} className="px-4 py-3 hover:bg-[#F8FAFC]" data-testid={`tft-request-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-[#0A2540]">{r.operator_label}</span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ color: meta.color, backgroundColor: meta.bg }}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </div>
                      <p className="text-xs text-[#64748B]">
                        Réf: <span className="font-mono text-[#0A2540]">{r.reference}</span> · {formatDate(r.created_at)}
                      </p>
                      {r.status === "rejected" && r.rejection_reason && (
                        <p className="text-xs text-[#EF4444] mt-1">Raison: {r.rejection_reason}</p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-[#0A2540]">+{(r.total_credit || 0).toLocaleString()} TFT</p>
                      <p className="text-[10px] text-[#64748B]">{(r.amount || 0).toLocaleString()} XAF + {r.bonus || 0} bonus</p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
